import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { DatepickerRange, Range } from '@xsolla/ui-calendar';
import { Button } from '@xsolla/ui-core';
import { Download } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { getUsers } from '../../../api';
import { UsersData } from '../../../api/users/types';
import { useConfig } from '../../../contexts/config';
import { useTableData } from '../../../hooks';
import AttributesModal from '../AttributesModal';
import { UserData } from '../AttributesModal/types';
import ExportUsersModal from '../ExportUsersModal';
import ExportUsersSuccessModal from '../ExportUsersModal/exportSuccessModal';
import Mailchimp from '../Mailchimp';
import { PaginationArrowButton } from './PaginationArrowButton';
import SearchField from './SearchField';
import { SelectGroups } from './SelectGroups';
import {
  FilterTableWrapper,
  FilterUserBlock,
  HorizontalSeparator,
  StyledFlexParentBlock,
  UserSettingsBlock,
  UsersNumber
} from './style';
import { DateState, RouteParams } from './types';
import { UserTable } from './UserListTable';
import { UserStatusSelect } from './UserStatusSelect';

type Props = RouteComponentProps;

const UserPageLimit = 20;

const UsersListTable = ({ match }: Props): JSX.Element => {
  const { projectId } = match.params as RouteParams;
  const { xsollaAnalytics } = useConfig();
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const [usersExported, setUsersExported] = useState<boolean>(false);
  const [userForAttributes, setUserAttributes] = useState<UserData | null>(null);
  const {
    data,
    setData,
    sortData,
    sortStatuses,
    fetching,
    setPage,
    page,
    otherParams,
    setParams,
    error
  } = useTableData<UsersData>(
    (params) =>
      getUsers(projectId, {
        limit: UserPageLimit,
        order_dir: 'desc',
        ...params
      }),
    UserPageLimit,
    {
      order_column: 'activity',
      order_dir: 'desc'
    }
  );

  const [onOpenExportModal, onCloseExportModal] = useModal(() => {
    xsollaAnalytics.sendClick('login_pa_module__users__export_modal', projectId);
    return (
      <ExportUsersModal
        projectId={projectId}
        onClose={onCloseExportModal}
        onSuccess={(email) => {
          xsollaAnalytics.sendClick('login_pa_module__users__export_modal', 'submit', { email });
          onOpenExportSuccessModal();
        }}
      />
    );
  });

  const [onOpenExportSuccessModal, onCloseExportSuccessModal] = useModal(() => {
    return (
      <ExportUsersSuccessModal
        onClose={() => {
          setUsersExported(true);
          onCloseExportModal();
          onCloseExportSuccessModal();
        }}
      />
    );
  });

  const [onOpenUserAttributesModal, onCloseUserAttributesModal] = useModal(() => {
    if (userForAttributes) {
      return (
        <AttributesModal
          projectId={projectId}
          onClose={onCloseUserAttributesModal}
          userData={userForAttributes}
        />
      );
    }
    return <></>;
  }, [userForAttributes]);

  const [filterDate, setFilterDate] = useState<DateState>({
    from: null,
    to: null
  });

  const selectDateHandle = (e: Range) => {
    if (!e.from && !e.to) {
      setParams((prev) => ({
        ...prev,
        activity_from: undefined,
        activity_to: undefined
      }));
      return;
    }
    setParams((prev) => ({
      ...prev,
      activity_from: moment(e.from).format('YYYY-MM-DD'),
      activity_to: moment(e.to).format('YYYY-MM-DD')
    }));
    setFilterDate({
      from: new Date(moment(e.from).format('YYYY-MM-DD')),
      to: new Date(moment(e.to).format('YYYY-MM-DD'))
    });
  };

  const UsersFound = () => (
    <UsersNumber data-test-id="user-number">
      {intl.formatMessage({ id: 'users.list-block.number-user' }, { number: data?.count || 0 })}
    </UsersNumber>
  );

  return (
    <>
      {/* ToDo: временно перенёс Mailchimp в блок с экспортом пользователей */}
      {/* <UsersListHeader /> */}

      <FilterTableWrapper>
        <UserStatusSelect setStatus={setParams} />
        <FilterUserBlock>
          <SelectGroups projectId={projectId} setParams={setParams} otherParams={otherParams} />
        </FilterUserBlock>
        <FilterUserBlock>
          <DatepickerRange
            options={{
              initialMonth: new Date(moment().format())
            }}
            onChange={selectDateHandle}
            value={filterDate}
            data-test-id="users-date-range"
          />
        </FilterUserBlock>

        <SearchField inputRef={inputRef} setParams={setParams} setPage={setPage} />
      </FilterTableWrapper>
      <UserSettingsBlock>
        {data?.count ? <UsersFound /> : null}
        <StyledFlexParentBlock>
          <Button look="link" palette="brand" size="sm" disabled data-test-id="user-manage-groups">
            {intl.messages['users.list-block.navigation.manage-groups']}
          </Button>
          <HorizontalSeparator />
          <Button
            look="link"
            palette="brand"
            size="sm"
            onClick={usersExported ? onOpenExportSuccessModal : onOpenExportModal}
            data-test-id="user-export">
            <Download />
            {intl.messages['users.list-block.navigation.export-users']}
          </Button>
          <HorizontalSeparator />
          {/* // ToDo: переделать управление таблицей */}
          <Mailchimp />
        </StyledFlexParentBlock>
      </UserSettingsBlock>
      <UserTable
        setUsersState={setData}
        fetching={fetching}
        tableData={data?.users}
        sortData={sortData}
        sortStatuses={sortStatuses}
        setUserAttributes={setUserAttributes}
        onOpenUserAttributesModal={onOpenUserAttributesModal}
        error={error}
      />
      <PaginationArrowButton setPage={setPage} page={page} userCount={data?.users.length} />
    </>
  );
};

export default withRouter(UsersListTable);
